import { Box, Tab, Tabs } from "@mui/material";
import type { FC } from "react";
import { Link, Route, Routes, useLocation } from "react-router";
import { PageContent, type PageContentProps } from "../../layout/components/page-content";

type RouteTab =
  | {
      type: "inferred";
      component: React.JSX.Element;
      data: {
        name: string;
        queryParamsToRemove?: string[];
      };
    }
  | {
      type: "explicit";
      component: React.JSX.Element;
      data: {
        name: string | React.JSX.Element;
        route: string;
        queryParamsToRemove?: string[];
      };
    };

interface RouteTabsProps extends PageContentProps {
  base: string;
  defaultPage: React.JSX.Element;
  tabs: RouteTab[];
  centered?: boolean;
  preserveQuery?: boolean;
}
export const RouteTabs: FC<RouteTabsProps> = ({
  defaultPage,
  tabs,
  base,
  centered,
  preserveQuery,
  ...pageContentProps
}) => {
  const location = useLocation();

  return (
    <PageContent {...pageContentProps}>
      <Tabs
        indicatorColor="primary"
        scrollButtons="auto"
        textColor="primary"
        value={location.pathname.replace(base, "")?.toUpperCase() ?? "/"}
        variant={centered ? "standard" : "scrollable"}
        centered={centered}
        sx={{ mt: 3 }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.type === "inferred" ? tab.data.name : tab.data.route}
            style={{ pointerEvents: "auto" }}
            label={tab.data.name}
            value={(tab.type === "inferred"
              ? normalizeName(tab.data.name)
              : tab.data.route
            ).toUpperCase()}
            sx={{ ml: 2 }}
            component={Link}
            to={`${base}${(tab.type === "inferred"
              ? normalizeName(tab.data.name)
              : tab.data.route
            ).toLowerCase()}${getPreservedSearch(
              location.search,
              preserveQuery,
              tab.data.queryParamsToRemove,
            )}`}
          />
        ))}
      </Tabs>
      <Box sx={{ mt: 4 }}>
        <Routes>
          <Route path="/" element={defaultPage} />
          {tabs.map((tab) => (
            <Route
              key={tab.type === "inferred" ? tab.data.name : tab.data.route}
              path={`${(tab.type === "inferred"
                ? normalizeName(tab.data.name)
                : tab.data.route
              ).toLowerCase()}/*`}
              element={tab.component}
            />
          ))}
        </Routes>
      </Box>
    </PageContent>
  );
};

function normalizeName(name: string): string {
  return encodeURIComponent(name.split(" ").join("_"));
}

function getPreservedSearch(
  search: string,
  preserveQuery?: boolean,
  queryParamsToRemove: string[] = [],
): string {
  if (!preserveQuery) return "";

  const searchParams = new URLSearchParams(search);
  queryParamsToRemove.forEach((queryParam) => searchParams.delete(queryParam));
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
}
